import React, { useState, useEffect, useContext } from 'react'
import clsx from 'clsx'
import { Text } from '../../../../ui'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { formatCurrency, formatDate } from '../../../../../utils/formatter'
import ModalEditMiContador from '../../../../modalDialogMiContador/ModalEditMiContador'
import ModalViewMiContador from '../../../../ui/tables/baseTable/ModalViewMiContador'
import SubTable from './SubTableComprobante'
import { OrganizationAPI } from '../../../../../../api/organization.api'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import GlobalContext from '../../../../../../context/global-context'
import dayjs from 'dayjs'
import { colsComprobante } from '../../../../data/headersTables'
import translations from '../../../../ui/traslation/traslationMiContador.json'
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'
import traslationAdmin from '../../../../ui/traslation/traslationAdministracion.json'
import traslationOriginTable from '../../../../ui/traslation/traslationOriginTable.json'

const tipoComprobanteMap = {
  1: 'Ingreso',
  2: 'Egreso',
  3: 'Traspaso'
}



function generateGlosa(
  tipoDocumento,
  folio,
  razonSocial,
  glosaOriginal,
  originId,
  comprobante,
  nombreCuenta,
  tipoComprobanteName
) {
  // 1) Si el usuario ya escribió una glosa:
  if (glosaOriginal) return glosaOriginal;

  // 2) Acción según Ingreso/Egreso:
  const action = tipoComprobanteName === 'Ingreso'
    ? 'Contabiliza'
    : tipoComprobanteName === 'Egreso'
      ? 'Contabiliza'
      : 'Contabiliza';

  // 3) Abreviatura (F, BH, …) según tipoDocumento
  const abbr = traslationAdmin.documentosContables[tipoDocumento];
  if (abbr && folio) {
    const razon = razonSocial?.trim() ?? '';
    // const cuenta = nombreCuenta ? ` ${nombreCuenta}` : '';
    // Ej: "Pago F/13618263 Proveedor… Prisa S.A. Proveedores Nacionales"
    return `${action} ${abbr}/${folio}${razon ? ` ${razon}` : ''}`;
  }

  // 4) Fallback por origen de tabla
  const originName = traslationOriginTable.originTable?.[originId];
  if (originName && comprobante != null) {
    const cuenta = nombreCuenta ? ` ${nombreCuenta}` : '';
    // Ej: "Pago VINCULOPROVEEDOR/504 Proveedores Nacionales"
    return `${action} ${originName} ${cuenta}`;
  }

  return '';
}

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const getMesTexto = (fecha) => {
  if (!fecha) return "";
  const d = new Date(fecha);
  return MESES[d.getMonth()]; // getMonth() 0-based
};

const transformGlosa = (glosa, fecha) => {
  const mes = getMesTexto(fecha);

  if (!glosa) return "";

  if (glosa === "sueldoBase") {
    return `Remuneraciones ${mes} Liquido`;
  }

  if (glosa.startsWith("Pago F/64 Remuneraciones")) {
    return `Pago Previred Remuneraciones ${mes}`;
  }

  // default: deja igual
  return glosa;
};

const renderGlosa = (row) => {
  // si ya viene seteada (por handleEditar) → devolver directo
  if (row.glosa) return transformGlosa(row.glosa, row.fecha);

  // aux correcto
  const aux =
    row.listaAuxiliar?.find(a => a.auxiliar === row.auxiliar) ||
    row.listaAuxiliar?.[0] ||
    {};

  const folio       = aux.folio       || row.numero || '';
  const razon       = aux.razonSocial || '';
  const originId    = aux.libroContableOriginTableId ?? row.libroContableOriginTableId;
  const comprob     = row.comprobante; 
  const cuentaName  = row.cuentaContable?.nombreCuenta || '';
  const tipoCompName = row.tipoComprobante?.name 
                     || tipoComprobanteMap[row.tipoComprobanteId] 
                     || '';

  // llamas a tu generador actual → pero ahora le pasamos glosa procesada
  const baseGlosa = generateGlosa(
    row.tipoDocumento,
    folio,
    razon,
    '', // glosaOriginal vacío
    originId,
    comprob,
    cuentaName,
    tipoCompName
  );

  return transformGlosa(baseGlosa, row.fecha);
};

const TableComprobante = ({
  comprobantes,
  rowsPerPage,
  setRowsPerPage,
  setComprobantes,
  apiGetDataTable,
  openEditModal,
  setOpenEditModal,
  setSwitchGetData
}) => {
  const [page, setPage] = useState(0)
  const [openViewModal, setOpenViewModal] = useState(false)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [selectedRowId, setSelectedRowId] = useState(null)
  const [expandedRowId, setExpandedRowId] = useState(null)
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const organizationAPI = new OrganizationAPI()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Estado dataEditar con glosa
  const [dataEditar, setDataEditar] = useState({ comprobanteId: '', validation: null, createUserId: null, glosa: '', comprobante: '' })
  const { ui } = useContext(GlobalContext)

  const filteredComprobantes = comprobantes.filter(comp => comp.validation !== -5)

  const handleToggleRow = row => {
    const hasData = row.listaAuxiliar?.[0]?.razonSocial && row.listaAuxiliar?.[0]?.rut
    if (hasData) setExpandedRowId(prev => (prev === row.id ? null : row.id))
  }

  useEffect(() => { setPage(0) }, [comprobantes])

  const handleSort = field => {
    if (sortColumn === field) {
      setSortDirection(dir => (dir === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumn(field)
      setSortDirection('asc')
    }
  }

  const getFormattedFecha = fecha => {
    if (!fecha) return ''
    const d = new Date(fecha)
    const y = d.getFullYear()
    const m = d.getMonth()
    const day = d.getDate()
    if (y === 2024 && m === 11 && day > 25) {
      d.setFullYear(2025, 0, 1)
    }
    return dayjs(d).format('DD-MM-YYYY')
  }

  const getCreateUserLabel = createUser => {
    if (Array.isArray(createUser)) {
      return createUser.map(u => `@${u.firstname}`).join(', ')
    }
    return `@${createUser?.firstname || 'Luca'}`
  }

  const getPeriodoKey = fecha => {
    if (typeof fecha !== 'string') return null
    const [year, month] = fecha.split('-')
    if (!year || !month) return null
    return `${year}-${month}`
  }

  const isRowBlocked = row => {
    const periodKey = getPeriodoKey(row?.fecha)
    return periodKey ? ui.isPeriodBlocked(periodKey) : false
  }

  const handleDelete = async () => {
    if (!selectedRowId) return
    const rowToDelete = comprobantes.find(c => c.id === selectedRowId)
    if (!rowToDelete) {
      ui.setDialog({
        title: translations.tableComprobante.titleNotFound,
        body: translations.actions.notFound,
        btnText: translations.actions.close,
        open: true
      })
      return
    }
    if (
      !(rowToDelete.listaAuxiliar?.[0]?.haber > 0 ||
        rowToDelete.listaAuxiliar?.[0]?.debe > 0 ||
        rowToDelete.estadoComprobante === 'Borrador')
    ) {
      ui.setDialog({
        title: translations.tableComprobante.titleNotAllowed,
        body: translations.actions.operationNotAllowed,
        btnText: translations.actions.close,
        open: true
      })
      return
    }
    const currentDate = dayjs().format('YYYY-MM-DD')
    const payload = {
      fecha: rowToDelete.fecha,
      tipo: rowToDelete.tipoComprobanteId,
      estado: -5,
      libros: rowToDelete.listaAuxiliar || [],
      vencimiento: rowToDelete.listaAuxiliar?.[0]?.vencimiento || '',
      razonSocial: rowToDelete.listaAuxiliar?.[0]?.razonSocial || '',
      folio: rowToDelete.listaAuxiliar?.[0]?.folio || '',
      rut: rowToDelete.listaAuxiliar?.[0]?.rut || '',
      glosa: rowToDelete.glosa || ''
    }
    try {
      if (rowToDelete.estadoComprobante === 'Borrador') {
        await organizationAPI.updateBorrador(rowToDelete.businessId, rowToDelete.id, payload)
      } else {
        await organizationAPI.updateLibroContable(rowToDelete.businessId, rowToDelete.id, {
          cuentaContableId: rowToDelete.cuentaContableId,
          validation: -5,
          glosa: '',
          vencimiento: currentDate
        })
      }
      setComprobantes(comprobantes.filter(c => c.id !== selectedRowId))
      ui.setDialog({
        title: translations.tableComprobante.titleSuccess,
        body: translations.actions.successDelete,
        btnText: translations.actions.close,
        open: true
      })
    } catch (err) {
      console.error(err)
      ui.setDialog({
        title: translations.tableComprobante.titleError,
        body: translations.actions.errorDelete,
        btnText: translations.actions.close,
        open: true
      })
    } finally { setOpenConfirmDialog(false) }
  }

  const handleOpenConfirmDialog = id => { setSelectedRowId(id); setOpenConfirmDialog(true) }
  const handleCloseConfirmDialog = () => { setOpenConfirmDialog(false); setSelectedRowId(null) }

  // Modificamos handleEditar para añadir glosa generada
  const handleEditar = (row, isBlocked) => {
  // 1) Extrae el aux correcto
  const aux =
    row.listaAuxiliar?.find(a => a.auxiliar === row.auxiliar) ||
    row.listaAuxiliar?.[0] || {};

  // 2) Valores clave
  const folio              = aux.folio       || row.numero || '';
  const razon              = aux.razonSocial || '';
  const originId           = aux.libroContableOriginTableId ?? row.libroContableOriginTableId;
  const comprobanteGeneral = row.comprobante;           // p.ej. 504 
  const nombreCuenta       = row.cuentaContable?.nombreCuenta || '';
  const tipoCompName       = row.tipoComprobante?.name   || tipoComprobanteMap[row.tipoComprobanteId];

  // 3) Genera la glosa
  const glosa = generateGlosa(
    row.tipoDocumento,
    folio,
    razon,
    row.glosa,
    originId,
    comprobanteGeneral,
    nombreCuenta,
    tipoCompName
  );

    setDataEditar({
      comprobanteId: row.id,
      validation: row.validation,
      createUserId: row.createUserId,
      glosa,
      comprobante: row.comprobante
    })
    isBlocked
      ? setOpenViewModal(true)
      : setOpenEditModal(true)
  }

  const sortedComprobantes = [...filteredComprobantes].sort((a, b) => {
    if (!sortColumn) return 0
    let va = a[sortColumn], vb = b[sortColumn]
    if (sortColumn === 'createUser') {
      va = Array.isArray(a.createUser)
        ? a.createUser.map(u => u.firstname).join(', ')
        : a.createUser?.firstname || ''
      vb = Array.isArray(b.createUser)
        ? b.createUser.map(u => u.firstname).join(', ')
        : b.createUser?.firstname || ''
    } else if (sortColumn === 'tipoComprobanteId') {
      va = a.tipoComprobante?.name || ''
      vb = b.tipoComprobante?.name || ''
    }
    if (va < vb) return sortDirection === 'asc' ? -1 : 1
    if (va > vb) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  const styles = {
    table: 'w-full min-w-[980px] table-auto text-[0.65rem] sm:text-[0.725rem] border-collapse leading-tight font-sans',
    thead: 'sticky top-0 z-10 bg-white',
    th: 'py-2 px-1 sm:px-2 w-auto max-w-[120px] text-center break-words border-b border-gray-200 text-[0.55rem] sm:text-[0.6rem] font-bold text-gray-500',
    tr: 'border-b border-gray-200 hover:bg-gray-100/50',
    td: 'py-2 px-1 sm:px-2 w-auto max-w-[200px] text-center break-words align-top',
    boldText: 'font-bold text-[0.6rem] sm:text-[0.65rem]'
  }

  const renderRowCard = row => {
    const isBlocked = isRowBlocked(row)
    const canDelete = !isBlocked && (
      row.estadoComprobante === 'Borrador' || row.listaAuxiliar?.[0]?.haber > 0 || row.listaAuxiliar?.[0]?.debe > 0
    ) && !(row.cuentaContable?.codigoCuenta === '1-1-4-1' && row.createUser === null)
    const hasAuxData = row.listaAuxiliar?.[0]?.razonSocial && row.listaAuxiliar?.[0]?.rut
    const tipoLabel =
      row.tipoComprobante?.name ||
      tipoComprobanteMap[row.tipoComprobanteId] ||
      row.estadoComprobante

    return (
      <div className='rounded-2xl border border-gray-100 bg-white shadow-sm p-3'>
        <div className='flex items-start justify-between gap-2'>
          <div>
            <Text className='text-xs font-bold'>
              {row.updatedAt && formatDate(row.updatedAt)}
            </Text>
            <Text className='text-[11px] text-gray-500'>
              {getCreateUserLabel(row.createUser)}
            </Text>
          </div>
          <div className='text-right'>
            <Text className='text-[10px] text-gray-400'>Fecha</Text>
            <Text className='text-xs font-semibold'>{getFormattedFecha(row.fecha)}</Text>
          </div>
        </div>

        <div className='mt-2 flex items-center justify-between gap-2'>
          <div>
            <Text className='text-[10px] text-gray-400'>Tipo</Text>
            <Text className='text-xs font-semibold'>{tipoLabel}</Text>
          </div>
          {hasAuxData && (
            <button
              type='button'
              className='flex items-center justify-center w-8 h-8 rounded-full bg-gray-100'
              onClick={() => handleToggleRow(row)}
              aria-label={expandedRowId === row.id ? 'Ocultar detalle' : 'Ver detalle'}
            >
              <KeyboardArrowDownIcon
                className={clsx('transition-transform', { 'rotate-180': expandedRowId === row.id })}
              />
            </button>
          )}
        </div>

        <div className='mt-2 grid grid-cols-2 gap-2 text-xs'>
          <div>
            <Text className='text-[10px] text-gray-400'>Comprobante</Text>
            <Text className='text-xs font-semibold'>{row.comprobante}</Text>
          </div>
          <div className='text-right'>
            <Text className='text-[10px] text-gray-400'>Total</Text>
            <Text className='text-sm font-bold'>{formatCurrency(row.total)}</Text>
          </div>
        </div>

        <div className='mt-2'>
          <Text className='text-[10px] text-gray-400'>Glosa</Text>
          <Text className='text-xs font-semibold'>{renderGlosa(row)}</Text>
        </div>

        <div className='mt-3 flex items-center justify-between'>
          <button
            type='button'
            className='flex items-center justify-center w-9 h-9 rounded-full bg-gray-100'
            onClick={() => handleEditar(row, isBlocked)}
            aria-label={isBlocked ? 'Ver movimiento' : 'Editar movimiento'}
          >
            {isBlocked ? (
              <RemoveRedEyeIcon sx={{ color: 'gray', fontSize: 18 }} />
            ) : (
              <EditOutlinedIcon sx={{ color: 'gray', fontSize: 18 }} />
            )}
          </button>
          <button
            type='button'
            onClick={() => {
              if (canDelete) handleOpenConfirmDialog(row.id)
            }}
            className={clsx(
              'flex items-center justify-center w-9 h-9 rounded-full shadow-full-xs',
              {
                'bg-accent1 hover:bg-red-600 cursor-pointer': canDelete,
                'bg-gray-300 cursor-not-allowed': !canDelete
              }
            )}
          >
            <HighlightOffIcon sx={{ color: canDelete ? 'white' : 'gray', fontSize: 18 }} />
          </button>
        </div>

        {expandedRowId === row.id && row.listaAuxiliar?.length > 0 && (
          <div className='mt-3'>
            <SubTable
              listaAuxiliar={row.listaAuxiliar.filter(obj => [1, 3, 12, 13, 14, 17, 18].includes(obj.libroContableOriginTableId))}
              fechaComprobante={row.fecha}
            />
          </div>
        )}
      </div>
    )
  }

  if (isMobile) {
    return (
      <div className='space-y-3 font-sans'>
        {(rowsPerPage > 0
          ? sortedComprobantes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          : sortedComprobantes
        ).map(row => (
          <div key={row.id}>{renderRowCard(row)}</div>
        ))}
        {openEditModal && (
          <ModalEditMiContador
            openModal={openEditModal}
            setOpenModal={setOpenEditModal}
            comprobanteId={dataEditar.comprobanteId}
            validation={dataEditar.validation}
            createUserId={dataEditar.createUserId}
            glosaInitial={dataEditar.glosa}
            nComprobante={dataEditar.comprobante}
            setSwitchGetData={setSwitchGetData}
            onComprobanteUpdated={apiGetDataTable}
            onClose={() => { setOpenEditModal(false); apiGetDataTable() }}
          />
        )}
        {openViewModal && (
          <ModalViewMiContador
            openModal={openViewModal}
            setOpenModal={setOpenViewModal}
            comprobanteId={dataEditar.comprobanteId}
            validation={dataEditar.validation}
            createUserId={dataEditar.createUserId}
          />
        )}
        <Dialog open={openConfirmDialog} onClose={handleCloseConfirmDialog}>
          <DialogTitle>{translations.tableComprobante.confirm}</DialogTitle>
          <DialogContent><p>{translations.actions.confirmDelete}</p></DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDialog} color='secondary'>{translations.actions.cancel}</Button>
            <Button onClick={handleDelete} color='primary'>{translations.actions.confirm}</Button>
          </DialogActions>
        </Dialog>
      </div>
    )
  }

  return (
    <div className='relative mt-2 bg-white font-sans'>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            {colsComprobante.map((col, i) => (
              <th
                key={i}
                className={clsx(styles.th, col.field && 'cursor-pointer')}
                onClick={() => col.field && handleSort(col.field)}
              >
                <span className='flex items-center justify-center'>
                  {col.label}
                  {sortColumn === col.field
                    ? sortDirection === 'asc'
                      ? <KeyboardArrowUpIcon fontSize='small' sx={{ color: '#000' }} />
                      : <KeyboardArrowDownIcon fontSize='small' sx={{ color: '#000' }} />
                    : col.field && <KeyboardArrowDownIcon fontSize='small' sx={{ color: '#ccc' }} />}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(rowsPerPage > 0
            ? sortedComprobantes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : sortedComprobantes
          ).map(row => {
            const isBlocked = isRowBlocked(row)
            const canDelete = !isBlocked && (
              row.estadoComprobante === 'Borrador' || row.listaAuxiliar?.[0]?.haber > 0 || row.listaAuxiliar?.[0]?.debe > 0
            ) && !(row.cuentaContable?.codigoCuenta === '1-1-4-1' && row.createUser === null)
            const hasAuxData = row.listaAuxiliar?.[0]?.razonSocial && row.listaAuxiliar?.[0]?.rut
            return (
              <React.Fragment key={row.id}>
                <tr className={styles.tr} onClick={() => handleToggleRow(row)}>
                  <td className={styles.td}><Text className={styles.boldText}>{row.updatedAt && formatDate(row.updatedAt)}</Text></td>
                  <td className={styles.td}><Text className={styles.boldText}>{getCreateUserLabel(row.createUser)}</Text></td>
                  <td className={styles.td}><Text className={styles.boldText}>{getFormattedFecha(row.fecha)}</Text></td>
                  <td className={styles.td}>
                    <div className="flex flex-col items-center">
                      <div className="flex items-center space-x-2">
                        <Text className={styles.boldText}>
                          {row.tipoComprobante?.name ||
                            tipoComprobanteMap[row.tipoComprobanteId] ||
                            row.estadoComprobante}
                        </Text>

                        {hasAuxData && (
                          <div className="px-0.5 py-0.5 bg-gray-200 rounded hover:bg-gray-300 cursor-pointer">
                            <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'inherit' }} />
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={styles.td}><Text className={styles.boldText}>{row.comprobante}</Text></td>
                  <td className={styles.td}><Text className={styles.boldText}>    {renderGlosa(row)}</Text></td>
                  <td className={styles.td}><Text className={styles.boldText}>{formatCurrency(row.total)}</Text></td>
                  <td className={styles.td}>
                    <div className='flex items-center justify-center space-x-3'>
                      <div className='bg-white shadow-full-xs rounded-full p-2 cursor-pointer' onClick={() => handleEditar(row, isBlocked)}>
                        {isBlocked ? <RemoveRedEyeIcon sx={{color:'gray'}}/> : <EditOutlinedIcon sx={{color:'gray'}}/>}
                      </div>
                      <div className={clsx('shadow-full-xs rounded-full p-2',{ 'bg-accent1 hover:bg-red-600 cursor-pointer':canDelete, 'bg-gray-300 cursor-not-allowed':!canDelete })} onClick={e=>{if(canDelete){e.stopPropagation(); handleOpenConfirmDialog(row.id)}}}>
                        <HighlightOffIcon sx={{color:canDelete?'white':'gray'}}/>
                      </div>
                    </div>
                  </td>
                </tr>
                {expandedRowId===row.id && row.listaAuxiliar?.length>0 && (
                  <tr>
                    <td colSpan={colsComprobante.length}>
                      <SubTable listaAuxiliar={row.listaAuxiliar.filter(obj=>[1,3,12,13,14,17,18,19].includes(obj.libroContableOriginTableId))} fechaComprobante={row.fecha}/>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>

      {openEditModal && (
        <ModalEditMiContador
          openModal={openEditModal}
          setOpenModal={setOpenEditModal}
          comprobanteId={dataEditar.comprobanteId}
          validation={dataEditar.validation}
          createUserId={dataEditar.createUserId}
          glosaInitial={dataEditar.glosa}
          nComprobante={dataEditar.comprobante}
          setSwitchGetData={setSwitchGetData}
          onComprobanteUpdated={apiGetDataTable}
          onClose={() => { setOpenEditModal(false); apiGetDataTable() }}
        />
      )}
      {openViewModal && (
        <ModalViewMiContador
          openModal={openViewModal}
          setOpenModal={setOpenViewModal}
          comprobanteId={dataEditar.comprobanteId}
          validation={dataEditar.validation}
          createUserId={dataEditar.createUserId}
        />
      )}
      <Dialog open={openConfirmDialog} onClose={handleCloseConfirmDialog}>
        <DialogTitle>{translations.tableComprobante.confirm}</DialogTitle>
        <DialogContent><p>{translations.actions.confirmDelete}</p></DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color='secondary'>{translations.actions.cancel}</Button>
          <Button onClick={handleDelete} color='primary'>{translations.actions.confirm}</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default TableComprobante
